import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, InputNumber, Modal, Select, Switch, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PauseIcon from "@mui/icons-material/Pause";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { TableCell } from "@mui/material";
import { AddButton, Button as CommonButton, CheckBox } from "src/Components";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
import TableTemplate, { IAdditional, IExtraTypeUIProps, ITableUI } from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import CellWithChart from "src/Tables/TableTemplate/CellWithChart";
import { ExtraType, Report } from "src/Interfaces/TableInterfaces";
import { getKeyValueJSX } from "src/Utils/commonJSX";
import ExecutionDetails from "./ExecutionDetails";
import ExecutionOrderBook from "./ExecutionOrderBook";
import ExecutionPlayPreview, { canSendPreview } from "./ExecutionPlayPreview";
import ExecutionTriggers from "./ExecutionTriggers";
import {
  addFuturesExecutionTask, changeFuturesExecutionTask, changeFuturesExecutionTasks,
  editFuturesExecutionTask, getFuturesExecutionAccounts,
  getFuturesExecutionChoices, getFuturesExecutionPlayPreviews,
  getFuturesExecutionSettings, getFuturesExecutionTasks,
  getFuturesExecutionNetVolumeChart, getFuturesExecutionNetVolume2Chart,
  getFuturesExecutionCurrentFactorChart,
  saveFuturesExecutionSettings, FuturesExecutionAccountChoice,
  FuturesExecutionChoice, FuturesExecutionSettings, FuturesExecutionTaskInput,
  FuturesExecutionTaskRow, FuturesExecutionTaskSearch, FuturesExecutionTaskOperation,
  FuturesExecutionOfferPreview,
} from "src/Requests/futuresMarketMaking";

const emptyTask: Partial<FuturesExecutionTaskInput> = {
  minimumTriggerIntervalSeconds: 0,
  referenceTolerancePercent: null,
  priceReference: "AutoSwitch",
  initialFactorMode: "Continues",
  initialFactor: null,
  orderType: "Open",
};

const numberWidth = { width: 120 };
const formatGroupedNumber = (value: string | number | undefined) => {
  const [integer, fraction] = String(value ?? "").split(".");
  const groupedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction === undefined ? groupedInteger : `${groupedInteger}.${fraction}`;
};
type TaskReport = Report<FuturesExecutionTaskRow> & {
  totalManual?: string;
  totalPortfo?: string;
  totalUsedBuyingPower?: string;
};

type ExecutionTableContextValue = {
  actions: IAdditional<FuturesExecutionTaskRow>;
  selectionEpoch: number;
  selectedTaskId?: number;
  selectTask: (row: FuturesExecutionTaskRow) => void;
};
const ExecutionTableContext = React.createContext<ExecutionTableContextValue | null>(null);

const ExecutionTaskTableView: React.FC<ITableUI<FuturesExecutionTaskRow>> = ({
  reports, onAction, sortingType, setSortingType, scrollKey,
}) => {
  const context = React.useContext(ExecutionTableContext);
  if (!context) return null;
  const getExtraTypeUI = ({ headerName, extraType, row, cellType }:
    IExtraTypeUIProps<FuturesExecutionTaskRow>) => {
    if (extraType !== ExtraType.WithChart ||
      (headerName !== "NetVolume" && headerName !== "NetVolume2" &&
        headerName !== "CurrentFactor")) return <></>;
    return <CellWithChart<FuturesExecutionTaskRow, Record<string, never>>
      title={`${row.accountName} - ${row.futureSymbol}`}
      cellType={cellType} headerName={headerName} row={row} extraType={extraType}
      chartGetter={() => headerName === "NetVolume"
        ? getFuturesExecutionNetVolumeChart(row.id)
        : headerName === "NetVolume2"
          ? getFuturesExecutionNetVolume2Chart(row.id)
          : getFuturesExecutionCurrentFactorChart(row.id)} />;
  };
  return <div className="w-full flex flex-col justify-start items-start gap-2">
    <div className="grid grid-cols-3 gap-4 w-full">
      {getKeyValueJSX("مجموع معاملات دستی", (reports as TaskReport).totalManual || "0")}
      {getKeyValueJSX("مجموع معاملات پلتفرم", (reports as TaskReport).totalPortfo || "0")}
      {getKeyValueJSX("مجموع قدرت خرید مورد استفاده", (reports as TaskReport).totalUsedBuyingPower || "0")}
    </div>
    <CommonTableUI key={context.selectionEpoch} reports={reports} onAction={onAction}
      sortingType={sortingType} setSortingType={setSortingType}
      AdditionalBody={context.actions} onTableRowClick={context.selectTask}
      activeRowId={context.selectedTaskId} scrollKey={scrollKey}
      getExtraTypeUI={getExtraTypeUI}
      withSelect tableBoxClassName="max-h-[350px]" />
  </div>;
};

const Execution: React.FC = () => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<FuturesExecutionSettings | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<FuturesExecutionTaskRow | null>(null);
  const [task, setTask] = useState<Partial<FuturesExecutionTaskInput>>(emptyTask);
  const [taskSaving, setTaskSaving] = useState(false);
  const [selectedTask, setSelectedTask] = useState<FuturesExecutionTaskRow | null>(null);
  const [triggerTaskId, setTriggerTaskId] = useState<number | null>(null);
  const [choices, setChoices] = useState<FuturesExecutionChoice[]>([]);
  const [accounts, setAccounts] = useState<FuturesExecutionAccountChoice[]>([]);
  const [baseFilter, setBaseFilter] = useState<string>();
  const [onlyActive, setOnlyActive] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [selectionEpoch, setSelectionEpoch] = useState(0);
  const [operationPending, setOperationPending] = useState(false);
  const visibleTasks = useRef<FuturesExecutionTaskRow[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewItems, setPreviewItems] = useState<FuturesExecutionOfferPreview[] | null>(null);
  const [previewAction, setPreviewAction] = useState<"play" | "resend">("play");
  const previewOnAction = useRef<(() => void) | null>(null);
  const search = useMemo<FuturesExecutionTaskSearch>(() => ({
    baseInstrumentId: baseFilter, isActive: onlyActive ? true : undefined,
  }), [baseFilter, onlyActive]);
  const futureSymbols = useMemo<Record<string, string>>(() =>
    Object.fromEntries(choices.map(choice => [choice.instrumentId, choice.symbol])), [choices]);

  useEffect(() => {
    getFuturesExecutionChoices().then(setChoices).catch(() => message.error("دریافت نمادهای آتی ناموفق بود"));
  }, []);

  useEffect(() => {
    if (!editorOpen) {
      setAccounts([]);
      return;
    }
    let current = true;
    setAccounts([]);
    getFuturesExecutionAccounts()
      .then(items => { if (current) setAccounts(items); })
      .catch(() => { if (current) message.error("دریافت اکانت‌ها ناموفق بود"); });
    return () => { current = false; };
  }, [editorOpen]);

  const openSettings = async () => {
    try {
      setSettings(await getFuturesExecutionSettings());
      setSettingsOpen(true);
    } catch { message.error("دریافت تنظیمات اجرا ناموفق بود"); }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSettingsSaving(true);
    try {
      setSettings(await saveFuturesExecutionSettings(settings));
      setSettingsOpen(false);
      message.success("تنظیمات ذخیره شد");
    } catch { message.error("ذخیره تنظیمات ناموفق بود"); }
    finally { setSettingsSaving(false); }
  };

  const changeSetting = <K extends keyof FuturesExecutionSettings>(key: K, value: FuturesExecutionSettings[K]) =>
    setSettings(current => current && { ...current, [key]: value });

  const changeBaseSetting = (baseInstrumentId: string,
    key: "firstStepTradeVolume" | "secondStepTradeVolume", value: number | null) =>
    setSettings(current => current && ({
      ...current,
      baseSettings: current.baseSettings.map(row => row.baseInstrumentId === baseInstrumentId
        ? { ...row, [key]: value } : row),
    }));

  const openNewTask = () => {
    setEditing(null);
    setTask({ ...emptyTask });
    setEditorOpen(true);
  };

  const openEditor = (row: FuturesExecutionTaskRow) => {
    setEditing(row);
    setTask({
      futureInstrumentId: row.futureInstrumentId,
      sahraUserId: row.sahraUserId,
      minimumTriggerIntervalSeconds: row.minimumTriggerIntervalSeconds,
      referenceTolerancePercent: row.referenceTolerancePercent,
      priceReference: row.priceReference,
      initialFactorMode: row.initialFactorMode === "Auto" ? "Continues" : row.initialFactorMode,
      initialFactor: row.initialFactor,
      orderType: row.orderType,
    });
    setEditorOpen(true);
  };

  const saveTask = async () => {
    if (!task.futureInstrumentId || !task.sahraUserId ||
        task.minimumTriggerIntervalSeconds == null || task.minimumTriggerIntervalSeconds < 0 ||
        task.referenceTolerancePercent != null && task.referenceTolerancePercent < 0 ||
        !task.priceReference || !task.initialFactorMode || !task.orderType ||
        task.initialFactor == null || task.initialFactor <= 0) {
      message.warning("فیلدهای فرم را کامل و معتبر وارد کنید");
      return;
    }
    setTaskSaving(true);
    try {
      const input = task as FuturesExecutionTaskInput;
      if (editing) await editFuturesExecutionTask(editing.id, input);
      else await addFuturesExecutionTask(input);
      setEditorOpen(false);
      setRefresh(x => x + 1);
      message.success("تسک ذخیره شد");
    } catch { message.error("ذخیره تسک ناموفق بود یا تسک تکراری است"); }
    finally { setTaskSaving(false); }
  };

  const executeOperation = async (ids: number[], action: FuturesExecutionTaskOperation,
    onAction: () => void, overrideMinimumTriggerInterval = false): Promise<boolean> => {
    setOperationPending(true);
    try {
        const previews = (action === "play" || action === "resend")
          ? Object.fromEntries((previewItems ?? [])
            .filter(item => item.snapshotId != null && item.programId != null)
            .map(item => [item.taskId, { snapshotId: item.snapshotId!, programId: item.programId!,
              factorSnapshotId: item.factorSnapshotId }]))
          : undefined;
        const result = ids.length === 1
          ? await changeFuturesExecutionTask(ids[0], action, previews?.[ids[0]], overrideMinimumTriggerInterval)
          : await changeFuturesExecutionTasks(ids, action, previews, overrideMinimumTriggerInterval);
        if (result.confirmationRequired) {
          Modal.confirm({ className: "futures-trigger-interval-confirm",
            title: "هشدار فاصله ارسال", okText: "تأیید ارسال", cancelText: "انصراف",
            content: `حداقل فاصله بین ارسال‌ها هنوز تمام نشده است (${result.remainingTriggerIntervalSeconds} ثانیه باقی مانده است). آیا مایل به ادامه هستید؟`,
            onOk: () => { void executeOperation(ids, action, onAction, true); } });
          return false;
        }
        if (result.changed) message.success(`${result.changed} تسک ${action === "delete" ? "حذف" : action === "play" ? "فعال" : action === "resend" ? "برای ارسال مجدد ثبت" : "متوقف"} شد`);
        if (result.notAllowed) message.warning(`${result.notAllowed} تسک خارج از دسترسی یا حذف‌شده بود`);
        if (result.unready) message.warning(`${result.unready} تسک برنامه یا قیمت تعادلی معتبر ندارد و تغییر نکرد`);
        if (result.fairPriceChangeRejected) message.warning(`${result.fairPriceChangeRejected} تسک به‌دلیل عبور تغییر قیمت تعادلی نسبت به آخرین تریگر موفق از سقف تنظیم‌شده، تریگر ناموفق ثبت کرد؛ این کنترل مستقل از بازه قیمت مجاز روز است`);
        if (result.dispatchFailed) message.error(`${result.dispatchFailed} تسک فعال شد اما سفارش‌های آن در صف ارسال قرار نگرفت`);
        if (action === "delete" && result.changed) {
          if (selectedTask && ids.includes(selectedTask.id)) setSelectedTask(null);
          setSelectionEpoch(value => value + 1);
        }
        if (action === "play" || action === "resend") setPreviewOpen(false);
        onAction();
        return true;
      } catch { message.error("انجام عملیات تسک ناموفق بود"); return false; }
      finally { setOperationPending(false); }
  };

  const openPlayPreview = async (ids: number[], action: "play" | "resend", onAction: () => void) => {
    setPreviewAction(action);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewItems(null);
    previewOnAction.current = onAction;
    try {
      setPreviewItems(await getFuturesExecutionPlayPreviews(ids));
    } catch { message.error("دریافت پیش‌نمایش سفارش‌ها ناموفق بود"); }
    finally { setPreviewLoading(false); }
  };

  const runOperation = async (ids: number[], action: FuturesExecutionTaskOperation,
    onAction: () => void) => {
    if (!ids.length || operationPending) return;
    if (action === "play" || action === "resend") return openPlayPreview(ids, action, onAction);
    if (action === "delete") {
      Modal.confirm({ title: "حذف تسک بازارگردانی آتی", content: `${ids.length} تسک حذف شود؟`,
        okText: "حذف", cancelText: "انصراف", okButtonProps: { danger: true },
        onOk: () => executeOperation(ids, action, onAction) });
    } else await executeOperation(ids, action, onAction);
  };

  const confirmPlay = async () => {
    const ids = previewItems?.filter(canSendPreview).map(item => item.taskId) ?? [];
    if (!ids.length || !previewOnAction.current) return;
    await executeOperation(ids, previewAction, previewOnAction.current);
  };

  const actionIcon = (label: string, icon: React.ReactNode, onClick: () => void,
    disabled = false, danger = false) => <button type="button" aria-label={label} title={label}
      className={`rounded p-1 transition-all ${danger ? "hover:bg-red-100 text-red-700" :
        "hover:bg-slate-300 text-sky-700"} disabled:cursor-not-allowed disabled:text-gray-300`}
      disabled={disabled || operationPending} onClick={event => { event.stopPropagation(); onClick(); }}>
      {icon}
    </button>;

  const actions: IAdditional<FuturesExecutionTaskRow> = {
    getHeader: ({ selectedRowsIds, onAction }) => <>
      <TableCell />
      <TableCell><div className="flex flex-row">
        {actionIcon("توقف گروهی", <PauseIcon />, () => {
          void runOperation(selectedRowsIds, "pause", onAction);
        }, !selectedRowsIds.length)}
        {actionIcon("فعال‌سازی گروهی", <PlayCircleIcon />, () => {
          void runOperation(selectedRowsIds, "play", onAction);
        }, !selectedRowsIds.length)}
      </div></TableCell>
      <TableCell />
      <TableCell><CommonButton className="table-btn" text="ارسال مجدد" variant="outlined"
        disabled={!selectedRowsIds.length || operationPending}
        onClick={() => { void runOperation(selectedRowsIds, "resend", onAction); }} /></TableCell>
      <TableCell>{actionIcon("حذف گروهی", <DeleteIcon fontSize="small" />, () => {
        void runOperation(selectedRowsIds, "delete", onAction);
      }, !selectedRowsIds.length, true)}</TableCell>
    </>,
    getRow: ({ row, onAction }) => <>
      <TableCell>{actionIcon("ویرایش", <EditIcon />, () => openEditor(row))}</TableCell>
      <TableCell className="!text-center">{row.isActive
          ? actionIcon("توقف", <PauseIcon />, () => { void runOperation([row.id], "pause", onAction); })
          : actionIcon("فعال‌سازی", <PlayCircleIcon />, () => {
              void runOperation([row.id], "play", onAction);
            })}</TableCell>
      <TableCell><CommonButton className="table-btn" text="تریگرها" variant="contained"
        onClick={() => setTriggerTaskId(row.id)} /></TableCell>
      <TableCell><CommonButton className="table-btn" text="ارسال مجدد" variant="contained"
        disabled={!row.isActive || operationPending}
        onClick={() => { void runOperation([row.id], "resend", onAction); }} /></TableCell>
      <TableCell>{actionIcon("حذف", <DeleteIcon fontSize="small" />, () => {
          void runOperation([row.id], "delete", onAction);
      }, false, true)}</TableCell>
    </>,
  };

  const baseOptions = Array.from(new Map(choices.map(x => [x.baseInstrumentId,
    { id: x.baseInstrumentId, name: x.baseSymbol }])).values());

  return <div className="w-full h-full" dir="rtl">
    <div className="flex flex-row justify-start items-start gap-3 w-full h-auto">
      <div className="min-w-0 flex-1">
        <ExecutionOrderBook task={selectedTask} />
      </div>
      <div className="flex shrink-0 flex-col justify-start items-start gap-3">
        <CommonButton onClick={openSettings} text="تنظیمات" variant="contained" />
      </div>
    </div>
    <div className="w-full h-max">
    <div className="pb-3 z-10 flex flex-row justify-between items-center"><AddButton onClick={openNewTask} /></div>
    <section className="w-full flex flex-col justify-start items-start gap-6 border border-gray-300 rounded p-3">
      <div className="flex flex-row justify-between items-center gap-3 w-full pl-20">
        <SelectableButtons label="نماد پایه" name="baseInsId" options={baseOptions}
          value={baseFilter ?? ""} onChange={(_, value) => setBaseFilter(value || undefined)} />
        <div className="flex justify-end w-full gap-8 pt-5">
          <button type="button" className="cursor-pointer"
            onClick={() => setRefresh(x => x + 1)} aria-label="بازخوانی تسک‌ها">
            <ReloadOutlined />
          </button>
          <CheckBox checked={onlyActive} label="نمایش فعال" name="justActive"
            onChange={(_, value) => setOnlyActive(value)} />
        </div>
      </div>
      <div className="overflow-auto order-table w-full">
        <ExecutionTableContext.Provider value={{ actions, selectionEpoch,
          selectedTaskId: selectedTask?.id,
          selectTask: row => setSelectedTask(row) }}>
          <TableTemplate getDataTable={getFuturesExecutionTasks} searchParameters={search}
            refreshTableTime={5000} silentRefreshKey={refresh} withSort
            onResponse={response => { visibleTasks.current = response.report; }}
            TableUI={ExecutionTaskTableView} />
        </ExecutionTableContext.Provider>
      </div>
    </section>
    </div>
    <ExecutionDetails task={selectedTask}
      symbols={futureSymbols} onClearSelection={() => setSelectedTask(null)} />
    <ExecutionTriggers taskId={triggerTaskId} onClose={() => setTriggerTaskId(null)} />

    <ExecutionPlayPreview open={previewOpen} loading={previewLoading} action={previewAction}
      submitting={operationPending} previews={previewItems} tasks={visibleTasks.current}
      onClose={() => setPreviewOpen(false)} onConfirm={() => { void confirmPlay(); }} />

    <Modal title="تنظیمات اجرای بازارگردانی آتی" open={settingsOpen} width={560}
      okText="ثبت" cancelText="انصراف" confirmLoading={settingsSaving}
      onOk={saveSettings} onCancel={() => setSettingsOpen(false)}>
      {settings && <div className="max-h-[72vh] overflow-y-auto space-y-7 py-4" dir="rtl">
        <section>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed border-collapse text-center text-sm">
              <colgroup><col className="w-[18%]" /><col className="w-[41%]" /><col className="w-[41%]" /></colgroup>
              <thead><tr className="bg-slate-50">
                <th className="border p-2">نماد پایه</th>
                <th className="border p-2">حجم پله اول تغییر ضریب</th>
                <th className="border p-2">حجم پله دوم تغییر ضریب</th>
              </tr></thead>
              <tbody>{settings.baseSettings.map(row => <tr key={row.baseInstrumentId}>
                <td className="border p-2">{row.baseSymbol}</td>
                {(["firstStepTradeVolume", "secondStepTradeVolume"] as const)
                  .map(key => <td className="border p-2" key={key}>
                    <InputNumber min={0} style={numberWidth} value={row[key]}
                      formatter={formatGroupedNumber}
                      onChange={value => changeBaseSetting(row.baseInstrumentId, key, value)} />
                  </td>)}
              </tr>)}</tbody>
            </table>
          </div>
        </section>
        <section>
          <h3 className="font-semibold mb-3">درصد تغییر ضریب</h3>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed border-collapse text-center text-sm">
              <colgroup><col className="w-[18%]" /><col className="w-[41%]" /><col className="w-[41%]" /></colgroup>
              <thead><tr className="bg-slate-50"><th className="border p-2">تغییر</th>
                <th className="border p-2">پله اول</th><th className="border p-2">پله دوم</th></tr></thead>
              <tbody>
                <tr><td className="border p-2">٪ کاهش</td>
                  <td className="border p-2"><InputNumber min={0} style={numberWidth}
                    formatter={formatGroupedNumber}
                    value={settings.decreaseFirstStepPercent}
                    onChange={value => changeSetting("decreaseFirstStepPercent", value)} /></td>
                  <td className="border p-2"><InputNumber min={0} style={numberWidth}
                    formatter={formatGroupedNumber}
                    value={settings.decreaseSecondStepPercent}
                    onChange={value => changeSetting("decreaseSecondStepPercent", value)} /></td></tr>
                <tr><td className="border p-2">٪ افزایش</td>
                  <td className="border p-2"><InputNumber min={0} style={numberWidth}
                    formatter={formatGroupedNumber}
                    value={settings.increaseFirstStepPercent}
                    onChange={value => changeSetting("increaseFirstStepPercent", value)} /></td>
                  <td className="border p-2"><InputNumber min={0} style={numberWidth}
                    formatter={formatGroupedNumber}
                    value={settings.increaseSecondStepPercent}
                    onChange={value => changeSetting("increaseSecondStepPercent", value)} /></td></tr>
              </tbody>
            </table>
          </div>
        </section>
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-end text-sm">
          <label className="flex flex-col gap-1">
            <span>زمان معتبر بودن ضریب در<br />توقف (دقیقه)</span>
            <InputNumber min={0} style={{ width: 190 }} value={settings.referenceValidityMinutes}
              formatter={formatGroupedNumber}
              onChange={value => changeSetting("referenceValidityMinutes", value)} /></label>
          <label className="flex flex-col gap-1">
            <span>حداکثر ٪ تغییر قیمت تعادلی<br />نسبت به آخرین تریگر موفق</span>
            <InputNumber min={0} style={{ width: 190 }} value={settings.maxFairPriceChangePercent}
              formatter={formatGroupedNumber}
              onChange={value => changeSetting("maxFairPriceChangePercent", value)} /></label>
          <label className="flex flex-col gap-1">
            <span>حداقل قدرت خرید مشتقه برای<br />سفارش‌گذاری کامل</span>
            <InputNumber min={0} style={{ width: 190 }} value={settings.minimumBuyingPower}
              formatter={formatGroupedNumber}
              onChange={value => changeSetting("minimumBuyingPower", value)} /></label>
          <label className="flex items-center gap-2">حذف سفارشات مضاعف
            <Switch checked={settings.removeExtraOrders}
              onChange={value => changeSetting("removeExtraOrders", value)} /></label>
        </section>
      </div>}
    </Modal>

    <Modal title={editing ? "ویرایش تسک بازارگردانی آتی" : "افزودن تسک بازارگردانی آتی"}
      open={editorOpen} width={620} okText="ثبت" cancelText="انصراف"
      confirmLoading={taskSaving} onOk={saveTask} onCancel={() => setEditorOpen(false)}
      footer={(_, { OkBtn, CancelBtn }) => <div className="flex items-center justify-between" dir="ltr">
        <OkBtn /><CancelBtn />
      </div>}>
      <div className="flex flex-col gap-3 py-4" dir="rtl">
        <label className="flex items-center justify-between gap-4">نماد
          <Select showSearch style={{ width: 340 }} placeholder="نماد" value={task.futureInstrumentId}
            onChange={value => setTask(current => ({ ...current, futureInstrumentId: value }))}
            options={choices.map(x => ({ value: x.instrumentId, label: x.symbol }))} />
        </label>
        <label className="flex items-center justify-between gap-4">نام اکانت
          <Select showSearch optionFilterProp="label" style={{ width: 340 }} placeholder="اکانت" value={task.sahraUserId}
            onChange={value => setTask(current => ({ ...current, sahraUserId: value }))}
            options={accounts.map(x => ({ value: x.id, label: x.name }))} />
        </label>
        <label className="flex items-center justify-between gap-4">حداقل فاصله دو تریگر (ثانیه)
          <InputNumber min={0} style={{ width: 340 }} value={task.minimumTriggerIntervalSeconds}
            onChange={value => setTask(current => ({ ...current, minimumTriggerIntervalSeconds: value ?? 0 }))} />
        </label>
        <label className="flex items-center justify-between gap-4">٪ تلورانس مبنا
          <InputNumber min={0} style={{ width: 340 }} value={task.referenceTolerancePercent}
            onChange={value => setTask(current => ({ ...current, referenceTolerancePercent: value }))} />
        </label>
        <label className="flex items-center justify-between gap-4">تعیین مبنا
          <Select style={{ width: 340 }} value={task.priceReference}
            onChange={value => setTask(current => ({ ...current, priceReference: value }))}
            options={[{ value: "VWSP", label: "VWSP" }, { value: "FTheor", label: "F-Theor" },
              { value: "AutoSwitch", label: "Auto" }]} />
        </label>
        <label className="flex items-center justify-between gap-4">مود ضریب مبنا
          <Select style={{ width: 340 }} value={task.initialFactorMode}
            onChange={value => setTask(current => ({ ...current, initialFactorMode: value }))}
            options={[{ value: "Manual", label: "دستی" },
              { value: "Continues", label: "پیوسته" }]} />
        </label>
        <label className="flex items-center justify-between gap-4">ضریب مبنای اولیه
          <InputNumber min={0.000001} style={{ width: 340 }} value={task.initialFactor}
            onChange={value => setTask(current => ({ ...current, initialFactor: value }))} />
        </label>
        <label className="flex items-center justify-between gap-4 mt-5">نوع سفارش
          <Select style={{ width: 340 }} value={task.orderType}
            onChange={value => setTask(current => ({ ...current, orderType: value }))}
            options={[{ value: "Open", label: "Open" }, { value: "Offset", label: "Offset" }]} />
        </label>
      </div>
    </Modal>
  </div>;
};

export default Execution;
